// Drive client for the PDF app — browse (Recent / My Drive / Shared with me /
// search) to open a PDF, fetch its bytes, and save back as a new version or
// as a new file. Full Drive CRUD lives in the drive-web SPA. Same patterns
// as EnterpriseNotes/web/src/api/driveApi.ts.

const baseUrl = import.meta.env.VITE_DRIVE_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error("VITE_DRIVE_BFF_BASE_URL is required.");
}

export interface DriveItem {
    id: string;
    parentId: string | null;
    kind: "Folder" | "File";
    name: string;
    contentType: string | null;
    sizeBytes: number;
    createdAt: string;
    updatedAt: string;
    lastAccessedAt?: string | null;
    isTrashed?: boolean;
    /** Set only when someone else owns the item and shared it with the caller. */
    ownerEmail?: string | null;
    /** The caller's access to a shared item. */
    role?: "viewer" | "editor" | null;
    /** False when a viewer's download is blocked. */
    canDownload?: boolean | null;
}

export interface FolderRef {
    id: string;
    parentId: string | null;
    name: string;
}

interface SharedItem {
    item: DriveItem;
    permissionId: string;
    sharedBy: string;
    sharedAt: string;
}

/** A failed Drive call. `message` is the server's ProblemDetails text when it sent one. */
export class DriveApiError extends Error {
    readonly status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = "DriveApiError";
        this.status = status;
    }
}

async function failure(res: Response, what: string): Promise<DriveApiError> {
    let message =
        res.status === 401 ? "Your session has expired — please sign in again."
            : res.status === 404 ? "That file no longer exists, or it isn't shared with you."
                : `${what} failed (${res.status}${res.statusText ? ` ${res.statusText}` : ""})`;
    try {
        const body = (await res.json()) as { title?: string; detail?: string };
        message = body.detail?.trim() || body.title?.trim() || message;
    } catch {
        /* not a JSON body — keep the status line */
    }
    return new DriveApiError(message, res.status);
}

/** True when `url` points at the Drive BFF — the only host we send the bearer token to. */
export function isDriveUrl(url: string): boolean {
    try {
        return new URL(url, window.location.href).origin === new URL(baseUrl as string).origin;
    } catch {
        return false;
    }
}

function authHeaders(token: string | undefined, accept?: string): Headers {
    const headers = new Headers();
    if (accept) headers.set("Accept", accept);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
}

async function getJson<T>(path: string, token: string | undefined, what: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(path, baseUrl);
    for (const [k, v] of Object.entries(params ?? {})) url.searchParams.set(k, v);
    let res: Response;
    try {
        res = await fetch(url.toString(), { headers: authHeaders(token, "application/json"), credentials: "omit" });
    } catch {
        throw new DriveApiError("Can't reach Drive — check your connection.", 0);
    }
    if (!res.ok) throw await failure(res, what);
    return (await res.json()) as T;
}

function fileForm(blob: Blob, fileName: string): FormData {
    const form = new FormData();
    form.append("file", new File([blob], fileName, { type: blob.type || "application/pdf" }));
    return form;
}

async function postForm(path: string, form: FormData, token: string | undefined, what: string): Promise<DriveItem> {
    let res: Response;
    try {
        res = await fetch(new URL(path, baseUrl).toString(), {
            method: "POST",
            headers: authHeaders(token, "application/json"),
            body: form,
            credentials: "omit"
        });
    } catch {
        throw new DriveApiError("Can't reach Drive — check your connection.", 0);
    }
    if (!res.ok) throw await failure(res, what);
    return (await res.json()) as DriveItem;
}

const enc = encodeURIComponent;

export const driveApi = {
    getItem: (id: string, token: string | undefined) =>
        getJson<DriveItem>(`/api/items/${enc(id)}`, token, "Opening the file"),

    /** A folder's contents — the caller's own, or a folder shared with them. Null = My Drive root. */
    listItems: (parentId: string | null, token: string | undefined) =>
        getJson<DriveItem[]>("/api/items", token, "Listing the folder", parentId ? { parentId } : undefined),

    /** Root → folder chain (inclusive) for breadcrumbs. */
    ancestors: (id: string, token: string | undefined) =>
        getJson<FolderRef[]>(`/api/items/${enc(id)}/ancestors`, token, "Loading the folder path"),

    /** The caller's files, most recently opened first. */
    listRecent: (take: number, token: string | undefined) =>
        getJson<DriveItem[]>("/api/recent", token, "Loading recent files", { take: String(take) }),

    /** Name search across the caller's items and items shared with them. */
    search: (q: string, take: number, token: string | undefined) =>
        getJson<DriveItem[]>("/api/search", token, "Searching Drive", { q, take: String(take) }),

    sharedWithMe: async (token: string | undefined): Promise<DriveItem[]> => {
        const rows = await getJson<SharedItem[]>("/api/shared-with-me", token, "Loading Shared with me");
        return rows.map((r) => r.item);
    },

    downloadBlob: async (id: string, token: string | undefined): Promise<Blob> => {
        let res: Response;
        try {
            res = await fetch(new URL(`/api/files/${enc(id)}/download`, baseUrl).toString(), {
                headers: authHeaders(token),
                credentials: "omit"
            });
        } catch {
            throw new DriveApiError("Can't reach Drive — check your connection.", 0);
        }
        if (!res.ok) throw await failure(res, "Downloading the file");
        return await res.blob();
    },

    /** Creates a NEW file in `parentId` (null = My Drive). Drive renames collisions to "name (2).pdf". */
    uploadBlob: (blob: Blob, fileName: string, parentId: string | null, token: string | undefined): Promise<DriveItem> => {
        const form = fileForm(blob, fileName);
        if (parentId) form.append("parentId", parentId);
        return postForm("/api/files", form, token, "Saving to Drive");
    },

    /** Saves new bytes as the next version of an existing file (owner or editor; same id and name). */
    uploadVersion: (fileId: string, blob: Blob, fileName: string, token: string | undefined): Promise<DriveItem> =>
        postForm(`/api/files/${enc(fileId)}/versions`, fileForm(blob, fileName), token, "Saving to Drive"),

    /**
     * Burns an Edit-mode session into the PDF (stateless; nothing is stored).
     * The Edit text covers become real redactions and the added text, shapes,
     * images and signatures become page content — see PdfToolEndpoints.cs.
     */
    flattenPdf: async (pdf: Blob, fileName: string, token: string | undefined): Promise<Blob> => {
        let res: Response;
        try {
            res = await fetch(new URL("/api/pdf/flatten", baseUrl).toString(), {
                method: "POST",
                headers: authHeaders(token, "application/pdf, application/problem+json"),
                body: fileForm(pdf, fileName),
                credentials: "omit"
            });
        } catch {
            throw new DriveApiError("Can't reach Drive — check your connection.", 0);
        }
        if (res.status === 404) {
            throw new DriveApiError("Drive can't apply PDF edits yet — the Drive service needs updating.", 404);
        }
        if (!res.ok) throw await failure(res, "Applying your edits");
        return new Blob([await res.blob()], { type: "application/pdf" });
    }
};

export function isPdf(item: Pick<DriveItem, "kind" | "name" | "contentType">): boolean {
    return item.kind === "File" && (/\.pdf$/i.test(item.name) || (item.contentType ?? "").toLowerCase() === "application/pdf");
}

/** Owners and editors save back to the same file; viewers Save As a copy. */
export function canSaveInPlace(item: Pick<DriveItem, "ownerEmail" | "role">): boolean {
    return !item.ownerEmail || item.role === "editor";
}
